import React from "react";

/* ─────────────────────────────────────────────────────────────────────────────
   Live indicators (29-Jul) — the app's two "something is happening" marks.
   Replaces Tailwind's animate-ping everywhere it was used.

     <LiveWake />   "this FEED is streaming" — RockLink's own pipeline flow mark
                    drifting downstream. Use in feed / activity-rail headers.
     <FreshDot />   "this ITEM is new"       — a still dot with one slow bloom.
                    Use as a per-row or per-control attention marker.

   The two are deliberately different shapes: a wake means throughput, a dot
   means "look here". Keeping them distinct is the point — don't swap one for
   the other to save a line.

   All styling lives in App.css (.rl-live / .rl-fresh). Colour is passed in so
   the emerald "new data just landed" state is a one-line swap.
   ───────────────────────────────────────────────────────────────────────────── */

// The canonical RockLink flow mark — identical geometry to the chevrons drawn
// between stages in the pipeline nav (Layout.js). If that glyph ever changes,
// change it here too: the whole point is that they speak the same language.
const FLOW_MARK = "M9 5l7 7-7 7";

/**
 * LiveWake — four flow marks shed downstream on a 2.4s loop.
 * @param color  stroke colour; swap to emerald to signal fresh data
 * @param idle   true → collapse to one static mark (feed seen / paused)
 * @param title  tooltip; when given the mark is exposed to screen readers
 */
export function LiveWake({ color = "#f97316", idle = false, title, className = "" }) {
  return (
    <span
      className={`rl-live ${className}`.trim()}
      style={{ color }}
      data-idle={idle ? "true" : undefined}
      title={title}
      role={title ? "img" : undefined}
      aria-label={title || undefined}
      aria-hidden={title ? undefined : "true"}
    >
      {[0, 1, 2, 3].map((i) => (
        <svg key={i} viewBox="0 0 24 24">
          <path d={FLOW_MARK} />
        </svg>
      ))}
    </span>
  );
}

/**
 * FreshDot — a still dot that blooms one soft ring every 2.8s.
 * @param size   px diameter (8 matches the old h-2 w-2 markers)
 * @param label  screen-reader text; pass "" to render none
 */
export function FreshDot({ color = "#10b981", size = 8, label = "New", className = "" }) {
  return (
    <>
      <span
        className={`rl-fresh ${className}`.trim()}
        style={{ width: size, height: size, color }}
        aria-hidden="true"
      />
      {label ? <span className="sr-only">{label}</span> : null}
    </>
  );
}
